import { FUNNEL_TYPES } from "../utils/contants";
import { FunnelContextArea } from "../types";
import { AuthType } from "../../../hooks/useAuthMutations";

/**
 * Hierarchical flow configuration for funnel progress indicators and messaging.
 * Structure: context → releaseType → userState → funnelType
 *
 * Text can be either a string or a function that takes authMethod
 * and returns appropriate text for login vs signup scenarios.
 */

// Text constants for all funnel messaging
export const TEXTS = {
  signUpFree: "Sign up for free",
  signUp: "Sign up to get started",
  logIn: "Log in to get started",
  choosePlan: "Choose your plan",
  goldOrBuy: "Get Gold or buy the book",
  saveProgress:
    "Save your reading progress, explore endlessly — Sign up for free!",
  becomeAMember:
    "Become a Gold Member to access this book and get exclusive discounts!",
  logInToContinue: "Log in to continue",
  buyToContinue: "Buy the book to continue",
  buyToContinueReading: "Buy the book to continue reading",
  buyToStartReading: "Buy the book to start reading",
  signUpComplete: "Sign up complete!",
  welcomeBack: "Welcome back!",
  goldAccess: "Get Gold Member access",
  goldDiscounts: "Get Gold Member discounts!",
  buyTheBook: "Buy the book",
  chooseAccess: "Choose how to access this book",
  alreadyOwnBook: "Sweet! You already own this book",
} as const;

type functionConfig = {
  authMethod?: AuthType;
  purchasedSub?: boolean;
  userOwnsBook?: boolean;
};

type flowTextFunction = (config: functionConfig) => string;
type flowBooleanFunction = (config: functionConfig) => boolean;
type flowNumberFunction = (config: functionConfig) => number;

type FlowConfigValue = {
  step?: number | flowNumberFunction;
  stepText?: string | flowTextFunction;
  text?: string | flowTextFunction;
  showIndicators?: boolean | flowBooleanFunction; // Controls progress bars (default: true)
};

type FunnelTypeConfig = {
  [K in FUNNEL_TYPES]: FlowConfigValue;
} & {
  default?: Partial<FlowConfigValue>;
};

type UserStateConfig = {
  startedLoggedOut?: Partial<FunnelTypeConfig>;
  startedLoggedIn?: Partial<FunnelTypeConfig>;
};

type ReleaseTypeConfig = {
  [key: string]: UserStateConfig;
};

type ContextConfig = {
  reader: ReleaseTypeConfig;
  details: ReleaseTypeConfig;
};

const FLOW_CONFIGS: ContextConfig = {
  reader: {
    subscriptionOnly: {
      startedLoggedOut: {
        default: {
          showIndicators: false,
        },
        authentication: {
          text: ({ authMethod }) =>
            authMethod === "login" ? TEXTS.logInToContinue : TEXTS.saveProgress,
        },
        subscription: {
          step: 2,
          text: TEXTS.becomeAMember,
        },
      },
      startedLoggedIn: {
        subscription: {
          text: TEXTS.becomeAMember,
          showIndicators: false,
        },
      },
    },
    subscriptionOrPurchase: {
      startedLoggedOut: {
        default: {
          step: 2,
        },
        authentication: {
          stepText: ({ authMethod }) =>
            authMethod === AuthType.login ? TEXTS.logIn : TEXTS.signUp,
        },
        subscription: {
          stepText: TEXTS.choosePlan,
          text: ({ authMethod }) =>
            authMethod === AuthType.login ? TEXTS.welcomeBack : "",
        },
        "pre-paywall": {
          stepText: TEXTS.goldOrBuy,
          text: ({ authMethod }) =>
            authMethod === AuthType.login ? TEXTS.welcomeBack : "",
        },
        "purchase-release": {
          stepText: ({ userOwnsBook }) =>
            userOwnsBook ? "" : TEXTS.buyTheBook,
          text: ({ authMethod, userOwnsBook }) => {
            if (userOwnsBook) {
              return TEXTS.alreadyOwnBook;
            }
            return authMethod === AuthType.login ? TEXTS.welcomeBack : "";
          },
        },
      },
      startedLoggedIn: {
        default: {
          showIndicators: false,
        },
        subscription: {
          text: TEXTS.goldDiscounts,
        },
        "purchase-release": {
          text: TEXTS.buyToContinue,
        },
        "pre-paywall": {
          text: TEXTS.chooseAccess,
        },
      },
    },
    premiumWithDiscount: {
      startedLoggedOut: {
        default: {
          step: 2,
        },
        authentication: {
          stepText: ({ authMethod }) =>
            authMethod === AuthType.login
              ? TEXTS.logInToContinue
              : TEXTS.signUpFree,
          text: TEXTS.buyToContinue,
        },
        "pre-paywall": {
          stepText: TEXTS.buyTheBook,
          text: ({ authMethod }) =>
            authMethod === AuthType.login
              ? TEXTS.welcomeBack
              : TEXTS.signUpComplete,
        },
        subscription: {
          text: TEXTS.becomeAMember,
        },
        "purchase-release": {
          showIndicators: ({ purchasedSub, userOwnsBook }) =>
            userOwnsBook ? false : purchasedSub ? false : true,
          stepText: ({ purchasedSub, userOwnsBook }) =>
            userOwnsBook ? "" : purchasedSub ? "" : TEXTS.buyTheBook,
          text: ({ authMethod, purchasedSub, userOwnsBook }) => {
            if (userOwnsBook) {
              return TEXTS.alreadyOwnBook;
            }
            return purchasedSub
              ? TEXTS.buyTheBook
              : authMethod === AuthType.login
                ? TEXTS.welcomeBack
                : TEXTS.signUpComplete;
          },
        },
      },
      startedLoggedIn: {
        default: {
          showIndicators: false,
        },
        "pre-paywall": {
          text: TEXTS.buyToContinueReading,
        },
        subscription: {
          text: TEXTS.becomeAMember,
        },
        "purchase-release": {
          text: TEXTS.buyTheBook,
        },
      },
    },
    premiumPurchaseOnly: {
      startedLoggedOut: {
        authentication: {
          stepText: ({ authMethod }) =>
            authMethod === AuthType.login ? TEXTS.logIn : TEXTS.signUpFree,
          text: TEXTS.buyToContinue,
        },
        "purchase-release": {
          step: 2,
          stepText: ({ userOwnsBook }) =>
            userOwnsBook ? "" : TEXTS.buyTheBook,
          text: ({ authMethod, userOwnsBook }) => {
            if (userOwnsBook) {
              return TEXTS.alreadyOwnBook;
            }
            return authMethod === AuthType.login
              ? TEXTS.welcomeBack
              : TEXTS.signUpComplete;
          },
        },
      },
      startedLoggedIn: {
        "purchase-release": {
          text: TEXTS.buyToContinueReading,
          showIndicators: false, // Hide for single-step flows
        },
      },
    },
  },
  details: {
    subscriptionOnly: {
      startedLoggedOut: {
        authentication: {
          stepText: ({ authMethod }) =>
            authMethod === AuthType.login ? TEXTS.logIn : TEXTS.signUp,
        },
        subscription: {
          step: 2,
          stepText: TEXTS.choosePlan,
        },
      },
      startedLoggedIn: {
        subscription: {
          text: TEXTS.becomeAMember,
          showIndicators: false, // Hide for single-step flows
        },
      },
    },
    subscriptionOrPurchase: {
      startedLoggedOut: {
        default: {
          step: 2,
        },
        authentication: {
          stepText: ({ authMethod }) =>
            authMethod === AuthType.login ? TEXTS.logIn : TEXTS.signUp,
        },
        subscription: {
          stepText: TEXTS.choosePlan,
          text: ({ authMethod }) =>
            authMethod === AuthType.login ? TEXTS.welcomeBack : "",
        },
        "purchase-release": {
          stepText: ({ userOwnsBook }) =>
            userOwnsBook ? "" : TEXTS.buyTheBook,
          text: ({ authMethod, userOwnsBook }) => {
            if (userOwnsBook) {
              return TEXTS.alreadyOwnBook;
            }
            return authMethod === AuthType.login ? TEXTS.welcomeBack : "";
          },
        },
      },
      startedLoggedIn: {
        default: {
          showIndicators: false,
        },
        subscription: {
          text: TEXTS.goldDiscounts,
        },
        "purchase-release": {
          text: TEXTS.buyToContinue,
        },
      },
    },
    premiumWithDiscount: {
      startedLoggedOut: {
        default: {
          step: 2,
        },
        authentication: {
          stepText: ({ authMethod }) =>
            authMethod === AuthType.login ? TEXTS.logIn : TEXTS.signUp,
        },
        subscription: {
          stepText: TEXTS.becomeAMember,
        },
        "purchase-release": {
          showIndicators: ({ purchasedSub, userOwnsBook }) =>
            userOwnsBook ? false : purchasedSub ? false : true,
          stepText: ({ purchasedSub, userOwnsBook }) =>
            userOwnsBook ? "" : purchasedSub ? "" : TEXTS.buyTheBook,
          text: ({ authMethod, purchasedSub, userOwnsBook }) => {
            if (userOwnsBook) {
              return TEXTS.alreadyOwnBook;
            }
            return purchasedSub
              ? TEXTS.buyToStartReading
              : authMethod === AuthType.login
                ? TEXTS.welcomeBack
                : TEXTS.signUpComplete;
          },
        },
      },
      startedLoggedIn: {
        default: {
          showIndicators: false,
        },
        subscription: {
          stepText: TEXTS.becomeAMember,
        },
        "purchase-release": {
          step: ({ purchasedSub }) => (purchasedSub ? 2 : 1),
          text: ({ purchasedSub }) =>
            !purchasedSub ? TEXTS.buyToStartReading : "",
          stepText: ({ purchasedSub }) =>
            purchasedSub ? TEXTS.buyToStartReading : "",
        },
      },
    },
    premiumPurchaseOnly: {
      startedLoggedOut: {
        authentication: {
          stepText: ({ authMethod }) =>
            authMethod === AuthType.login ? TEXTS.logIn : TEXTS.signUp,
        },
        "purchase-release": {
          step: 2,
          stepText: ({ userOwnsBook }) =>
            userOwnsBook ? "" : TEXTS.buyTheBook,
          text: ({ authMethod, userOwnsBook }) => {
            if (userOwnsBook) return TEXTS.alreadyOwnBook;
            return authMethod === AuthType.login
              ? TEXTS.welcomeBack
              : TEXTS.signUpComplete;
          },
        },
      },
      startedLoggedIn: {
        "purchase-release": {
          text: TEXTS.buyToStartReading,
          showIndicators: false, // Hide for single-step flows
        },
      },
    },
  },
};

/**
 * Find a flow configuration based on the funnel context and user state.
 * Handles dynamic text resolution for login vs signup scenarios.
 */
export function findFlowConfig(
  funnelType: FUNNEL_TYPES,
  context: "reader" | "details",
  releaseType: string,
  userState: "startedLoggedIn" | "startedLoggedOut",
  authMethod?: AuthType,
  purchasedSub?: boolean,
  userOwnsBook?: boolean,
):
  | {
      stepNumber: number;
      stepText: string;
      text: string;
      showIndicators: boolean;
    }
  | undefined {
  // Navigate the hierarchy
  const defaultConfig =
    FLOW_CONFIGS[context]?.[releaseType]?.[userState]?.default;
  const config =
    FLOW_CONFIGS[context]?.[releaseType]?.[userState]?.[funnelType];

  const mergedConfig = {
    ...defaultConfig,
    ...config,
  };

  if (!config) {
    return undefined;
  }

  const {
    step = 1,
    stepText = "",
    text = "",
    showIndicators = true,
  } = mergedConfig;

  // Authentication is always step 1, regardless of defaults
  const stepNumber =
    funnelType === FUNNEL_TYPES.authentication
      ? 1
      : typeof step === "function"
        ? step({ authMethod, userOwnsBook, purchasedSub })
        : step;

  return {
    stepNumber,
    showIndicators:
      typeof showIndicators === "function"
        ? showIndicators({
            authMethod: authMethod || AuthType.signup,
            purchasedSub,
            userOwnsBook,
          })
        : showIndicators,
    stepText:
      typeof stepText === "function"
        ? stepText({
            authMethod: authMethod || AuthType.signup,
            purchasedSub,
            userOwnsBook,
          })
        : stepText,
    text:
      typeof text === "function"
        ? text({
            authMethod: authMethod || AuthType.signup,
            purchasedSub,
            userOwnsBook,
          })
        : text,
  };
}

/**
 * Map context area to simplified context string
 */
export function mapContextArea(
  contextArea: FunnelContextArea,
): "reader" | "details" | null {
  let result: "reader" | "details" | null;

  switch (contextArea) {
    case FunnelContextArea.reader:
    case FunnelContextArea.verticalReader:
    case FunnelContextArea.readerPreviewBanner:
    case FunnelContextArea.readerOverlay:
      result = "reader";
      break;
    case FunnelContextArea.comicDetails:
    case FunnelContextArea.releaseDetails:
    case FunnelContextArea.featureCard:
    case FunnelContextArea.heroCard:
      result = "details";
      break;
    default:
      result = null;
      break;
  }

  return result;
}

/**
 * Determine release type from release object.
 * Returns camelCase keys for cleaner object navigation.
 */
export function getReleaseType(release?: any): string {
  // Type 1: Subscription-only
  if (!release || (!release.premium_only && !release.ppb_price_point)) {
    return "subscriptionOnly";
  }
  // Type 2: Subscription OR Purchase
  if (!release.premium_only && release.ppb_price_point) {
    return "subscriptionOrPurchase";
  }
  // Type 3: Premium with discount
  if (release.premium_only && release.ppb_gold_price_point) {
    return "premiumWithDiscount";
  }
  // Type 4: Premium purchase only
  if (release.premium_only && !release.ppb_gold_price_point) {
    return "premiumPurchaseOnly";
  }

  return "unknown";
}
